import { useState, useEffect, useRef } from "react";
import { getAnswer } from "../../data/moneybotKB";
import { track } from "../../lib/track";
import { BUDGET_CATS, getMonthLabel, computeHealth } from "../../data/budgetCats";
import { EMPTY_CATS } from "../../lib/storage";
import { askMoneyBot, MoneyBotError, OFFLINE_MSG, BUSY_MSG, TIMEOUT_MSG, previewDebug, type ChatMsg } from "../../lib/moneybot";
import ChatMarkdown from "../../components/ChatMarkdown";

interface Props {
  trackerData: any;
  budgetData: any;
  proContext: "tracker" | "budget" | null;
  onViewFullTracker: () => void;
  onViewFullBudget: () => void;
  onPersistTracker: (data: any) => void;
  onPersistBudget: (data: any) => void;
}

interface SetupState {
  mode: "tracker" | "budget";
  step: number;
  values: Record<string, number>;
}

// ── Setup question definitions ────────────────────────────────────────────────

const TRACKER_STEPS: Array<{ key: string; emoji: string; label: string; isDebt: boolean; q: (v: Record<string, number>) => string }> = [
  { key: "savings",    emoji: "🏦", label: "Cash Savings",     isDebt: false,
    q: () => "What's your total cash savings balance?\n(Add up all checking + savings accounts)\n\nExample: 3200" },
  { key: "invest",     emoji: "📈", label: "Investments",      isDebt: false,
    q: () => "How much do you have in investments?\n(401k, IRA, brokerage, crypto — add them all up)\n\nEnter 0 if you haven't started yet." },
  { key: "emergency",  emoji: "🛡️", label: "Emergency Fund",  isDebt: false,
    q: () => "How much is in your emergency fund?\n(Money set aside ONLY for emergencies, separate from regular savings)\n\nEnter 0 if you haven't started one." },
  { key: "creditCard", emoji: "💳", label: "Credit Card Debt", isDebt: true,
    q: () => "Total credit card debt?\n(Add up all balances you carry month to month)\n\nEnter 0 if you pay them off in full every month." },
  { key: "student",    emoji: "🎓", label: "Student Loans",    isDebt: true,
    q: () => "Total student loan balance?\n(Federal + private loans combined)\n\nEnter 0 if none." },
  { key: "carLoan",    emoji: "🚗", label: "Car Loan",          isDebt: true,
    q: () => "Remaining car loan balance?\n\nEnter 0 if paid off or no car loan." },
  { key: "other",      emoji: "💸", label: "Other Debt",        isDebt: true,
    q: () => "Any other debts?\n(Medical bills, personal loans, money owed to people)\n\nEnter 0 if none." },
];

const BUDGET_STEPS: Array<{ key: string; emoji: string; label: string; isIncome?: boolean; q: (v: Record<string, number>) => string }> = [
  { key: "income",        emoji: "💵", label: "Monthly Income",    isIncome: true,
    q: () => "What's your monthly take-home pay?\n(What actually hits your bank account after taxes)\n\nExample: if $50k salary → roughly $3,400/month take-home" },
  { key: "housing",       emoji: "🏠", label: "Housing",
    q: () => "Monthly housing cost?\n(Rent or mortgage payment)\n\nExample: 1200" },
  { key: "food",          emoji: "🍔", label: "Food & Groceries",
    q: () => "Monthly food budget?\n(Groceries + dining out combined)\n\nExample: 400" },
  { key: "transport",     emoji: "🚗", label: "Transportation",
    q: () => "Monthly transportation cost?\n(Gas, car payment, insurance, transit)\n\nExample: 250" },
  { key: "entertainment", emoji: "🎬", label: "Entertainment",
    q: () => "Monthly entertainment budget?\n(Streaming, subscriptions, dining out, hobbies)\n\nExample: 150" },
  { key: "savings",       emoji: "🏦", label: "Savings",
    q: (v) => `Monthly savings target?\n(Aim for at least 20% of your income${v.income > 0 ? ` = $${Math.round(v.income * 0.20).toLocaleString()}/month` : ""})\n\nExample: 500` },
  { key: "health",        emoji: "💪", label: "Health",
    q: () => "Monthly health costs?\n(Medical, dental, gym, prescriptions)\n\nEnter 0 if fully covered by employer." },
  { key: "other",         emoji: "📦", label: "Other",
    q: () => "Monthly other expenses?\n(Personal care, household supplies, anything else)\n\nExample: 200" },
];

// ── Amount parser ─────────────────────────────────────────────────────────────

function parseAmount(raw: string): number | null {
  const lower = raw.toLowerCase().trim();
  if (["0", "none", "skip", "n/a", "no", "nope", "nothing", "zero", "nada"].includes(lower)) return 0;
  const kMatch = lower.match(/^\$?([\d,]+\.?\d*)k$/);
  if (kMatch) return parseFloat(kMatch[1].replace(/,/g, "")) * 1000;
  const cleaned = lower.replace(/[^0-9.]/g, "");
  if (!cleaned) return null;
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
}

// ── Greeting generators ───────────────────────────────────────────────────────

function trackerSetupGreeting(): string {
  return [
    "Hi! I'm your Tracker Assistant 🤖",
    "",
    "I'll build your personalized wealth tracker. I just need 7 quick numbers — takes about 2 minutes.",
    "",
    "Here's what we'll cover:",
    "",
    "💰 Assets (what you own):",
    "   1. Cash Savings",
    "   2. Investments",
    "   3. Emergency Fund",
    "",
    "💳 Debts (what you owe):",
    "   4. Credit Card Debt",
    "   5. Student Loans",
    "   6. Car Loan",
    "   7. Other Debt",
    "",
    "Let's go! — Question 1 of 7:",
    "",
    TRACKER_STEPS[0].q({}),
  ].join("\n");
}

function budgetSetupGreeting(today: string): string {
  return [
    "Hi! I'm your Budget Assistant 🤖",
    "",
    `I'll build your personalized ${getMonthLabel(today)} budget. I need 8 quick numbers — takes about 2 minutes.`,
    "",
    "Here's what we'll set up:",
    "",
    "   1. 💵 Monthly take-home pay",
    "   2. 🏠 Housing",
    "   3. 🍔 Food & Groceries",
    "   4. 🚗 Transportation",
    "   5. 🎬 Entertainment",
    "   6. 🏦 Savings target",
    "   7. 💪 Health",
    "   8. 📦 Other expenses",
    "",
    "Let's go! — Question 1 of 8:",
    "",
    BUDGET_STEPS[0].q({}),
  ].join("\n");
}

function trackerDataGreeting(trackerData: any): string {
  const cats = trackerData.categories;
  const snap = trackerData.history[trackerData.history.length - 1].snapshot;
  let nw = 0, totalAssets = 0, totalDebts = 0;
  const assetLines: string[] = [];
  const debtLines: string[] = [];

  Object.entries(cats).forEach(([k, c]: [string, any]) => {
    const v = snap[k] || 0;
    if (c.isAsset) {
      nw += v; totalAssets += v;
      if (v > 0) assetLines.push(`  ${c.emoji} ${c.label}: $${Math.round(v).toLocaleString()}`);
    } else {
      nw -= v; totalDebts += v;
      if (v > 0) debtLines.push(`  ${c.emoji} ${c.label}: $${Math.round(v).toLocaleString()}`);
    }
  });

  const lines = [
    "Hi! I'm your Tracker Assistant 🤖",
    "",
    `📊 Net Worth: ${nw >= 0 ? "$" : "-$"}${Math.abs(Math.round(nw)).toLocaleString()}`,
    "",
    `💰 Assets — $${Math.round(totalAssets).toLocaleString()} total:`,
    ...(assetLines.length ? assetLines : ["  (none recorded yet)"]),
    "",
    `💳 Debts — $${Math.round(totalDebts).toLocaleString()} total:`,
    ...(debtLines.length ? debtLines : ["  None — debt free! 🎉"]),
  ];

  if (totalDebts > 0) {
    lines.push(
      "",
      nw >= 0
        ? "Your net worth is positive — you own more than you owe. Keep growing it!"
        : "Your net worth is currently negative — debt outweighs assets. Attacking your highest-interest debt first is the fastest fix."
    );
  } else {
    lines.push("", "No debt at all — that's a huge advantage. Your focus now is growing your assets and investing.");
  }

  lines.push("", "Ask me anything, or tap a quick question below.");
  return lines.join("\n");
}

function budgetDataGreeting(budgetData: any, today: string): string {
  const md = budgetData.months[today];
  const income = md.income || 0;
  const cats = computeCatsWithActual(md);
  const h = computeHealth(cats);
  const statusLabel = h.status === "over" ? "🚨 Over Budget" : h.status === "close" ? "⚠️ Getting Close" : "✅ On Track";

  const lines = [
    "Hi! I'm your Budget Assistant 🤖",
    "",
    `💵 Income: $${income.toLocaleString()}/month`,
    `📊 ${getMonthLabel(today)}: $${Math.round(h.totalActual).toLocaleString()} of $${Math.round(h.totalBudget).toLocaleString()} budgeted (${Math.round(h.pct)}% — ${statusLabel})`,
    "",
    "Category breakdown:",
  ];

  for (const cat of BUDGET_CATS) {
    const cv = cats[cat.id];
    if (!cv || (cv.budget === 0 && cv.actual === 0)) continue;
    const remaining = cv.budget - cv.actual;
    const icon = cv.actual > cv.budget ? "🚨" : cv.actual / cv.budget > 0.85 ? "⚠️" : "✅";
    lines.push(
      `  ${cat.emoji} ${cat.label}: $${Math.round(cv.actual).toLocaleString()}/$${Math.round(cv.budget).toLocaleString()} — ${remaining >= 0 ? `$${Math.round(remaining).toLocaleString()} left` : `$${Math.round(Math.abs(remaining)).toLocaleString()} over`} ${icon}`
    );
  }

  const overCats = BUDGET_CATS.filter(
    (c) => (cats[c.id]?.actual || 0) > (cats[c.id]?.budget || 0) && (cats[c.id]?.budget || 0) > 0
  );
  if (overCats.length > 0) {
    lines.push("", `You're over budget in ${overCats.map((c) => c.label).join(" and ")}. Want suggestions to cut back?`);
  } else if (h.status === "good" && income > 0) {
    lines.push("", `You're on track! $${Math.round(income - h.totalActual).toLocaleString()} of your income unspent so far.`);
  }

  lines.push("", "Ask me anything or tap a quick question below.");
  return lines.join("\n");
}

function generalGreeting(trackerData: any, budgetData: any, today: string): string {
  const hasTracker = trackerData?.history?.length > 0;
  const hasBudget = !!budgetData?.months?.[today];

  if (!hasTracker && !hasBudget) {
    return "Hi! I'm MoneyBot 🤖 Your personal AI financial advisor.\n\nYou haven't set up your tracker or budget yet. Tap the Tracker tab to build your wealth tracker, or the Budget tab to set up your monthly budget — I'll guide you through both in about 2 minutes each.\n\nOr ask me any financial question below!";
  }

  const parts = ["Hi! I'm MoneyBot 🤖 Here's your financial picture:\n"];

  if (hasTracker) {
    const cats = trackerData.categories;
    const snap = trackerData.history[trackerData.history.length - 1].snapshot;
    let nw = 0, assets = 0, debts = 0;
    Object.entries(cats).forEach(([, c]: [string, any]) => {
      const v = snap[Object.keys(cats).find((k) => cats[k] === c) as string] || 0;
      if (c.isAsset) { nw += v; assets += v; } else { nw -= v; debts += v; }
    });
    // Recalculate properly
    nw = 0; assets = 0; debts = 0;
    Object.entries(cats).forEach(([k, c]: [string, any]) => {
      const v = snap[k] || 0;
      if (c.isAsset) { nw += v; assets += v; } else { nw -= v; debts += v; }
    });
    parts.push(`📈 Net Worth: ${nw >= 0 ? "$" : "-$"}${Math.abs(Math.round(nw)).toLocaleString()} | Assets $${Math.round(assets).toLocaleString()} · Debt $${Math.round(debts).toLocaleString()}`);
  }

  if (hasBudget) {
    const md = budgetData.months[today];
    const h = computeHealth(computeCatsWithActual(md));
    const sl = h.status === "over" ? "🚨 Over Budget" : h.status === "close" ? "⚠️ Getting Close" : "✅ On Track";
    parts.push(`💰 ${getMonthLabel(today)}: ${Math.round(h.pct)}% used — ${sl}`);
  }

  parts.push("\nWhat would you like to work on? Ask me anything or tap a question below.");
  return parts.join("\n");
}

// ── Derive actual spending from transactions (budget data only stores budget + txns) ──

function computeCatsWithActual(md: any): Record<string, { budget: number; actual: number }> {
  const cats = md?.categories ?? {};
  const transactions: Transaction[] = md?.transactions ?? [];
  const result: Record<string, { budget: number; actual: number }> = {};
  for (const cat of BUDGET_CATS) {
    result[cat.id] = {
      budget: cats[cat.id]?.budget ?? 0,
      actual: transactions.filter(t => t.category === cat.id).reduce((s, t) => s + t.amount, 0),
    };
  }
  return result;
}

// ── Financial context for API ─────────────────────────────────────────────────

function buildFinancialContext(trackerData: any, budgetData: any): string {
  const lines: string[] = ["[User's actual financial data — reference these numbers directly in your advice]"];

  if (trackerData?.history?.length > 0) {
    const cats = trackerData.categories;
    const snap = trackerData.history[trackerData.history.length - 1].snapshot;
    let nw = 0;
    const assets: string[] = [];
    const debts: string[] = [];
    Object.entries(cats).forEach(([k, c]: [string, any]) => {
      const v = snap[k] || 0;
      if (c.isAsset) {
        nw += v;
        if (v > 0 || c.target > 0) assets.push(`${c.label} $${Math.round(v).toLocaleString()}${c.target > 0 ? ` [goal $${Math.round(c.target).toLocaleString()}]` : ""}`);
      } else {
        nw -= v;
        if (v > 0) debts.push(`${c.label} $${Math.round(v).toLocaleString()}`);
      }
    });
    lines.push(
      `Wealth Tracker (${trackerData.history.length} check-ins): Net Worth ${nw >= 0 ? "$" : "-$"}${Math.abs(Math.round(nw)).toLocaleString()}` +
        (assets.length ? ` | Assets: ${assets.join(", ")}` : "") +
        (debts.length ? ` | Debts: ${debts.join(", ")}` : " | No debts")
    );
  } else {
    lines.push("Wealth Tracker: not set up yet");
  }

  const today = new Date().toISOString().slice(0, 7);
  const md = budgetData?.months?.[today];
  if (md) {
    const income = md.income || 0;
    const cats = computeCatsWithActual(md);
    const h = computeHealth(cats);
    const overCats = BUDGET_CATS.filter(
      (c) => (cats[c.id]?.actual || 0) > (cats[c.id]?.budget || 0) && (cats[c.id]?.budget || 0) > 0
    ).map((c) => c.label);
    const catDetail = BUDGET_CATS.filter((c) => (cats[c.id]?.budget || 0) > 0 || (cats[c.id]?.actual || 0) > 0)
      .map((c) => `${c.label} $${Math.round(cats[c.id]?.actual || 0)}/$${Math.round(cats[c.id]?.budget || 0)}`)
      .join(", ");
    lines.push(
      `Budget ${getMonthLabel(today)}: Income $${income.toLocaleString()} | Spent $${Math.round(h.totalActual).toLocaleString()} of $${Math.round(h.totalBudget).toLocaleString()} (${Math.round(h.pct)}% — ${h.status === "over" ? "OVER BUDGET" : h.status === "close" ? "getting close" : "on track"})` +
        (overCats.length ? ` | Over budget: ${overCats.join(", ")}` : "") +
        (catDetail ? ` | ${catDetail}` : "")
    );
  } else {
    lines.push(`Budget: not set up for ${getMonthLabel(today)}`);
  }

  return lines.join("\n");
}

// ── Mini summary bar ──────────────────────────────────────────────────────────

function MiniSummary({ trackerData, budgetData, proContext, onViewFullTracker, onViewFullBudget }: Props) {
  const today = new Date().toISOString().slice(0, 7);
  const hasTracker = trackerData?.history?.length > 0;
  const md = budgetData?.months?.[today];

  const btnStyle: React.CSSProperties = {
    background: "#1e293b", border: "1px solid #334155", borderRadius: 10,
    padding: "7px 12px", fontSize: 11, fontWeight: 800, color: "#94a3b8",
    cursor: "pointer", whiteSpace: "nowrap",
  };

  if (proContext === "tracker") {
    if (!hasTracker) {
      return (
        <div style={{ padding: "10px 14px", background: "#0f172a", borderBottom: "1px solid #1e293b", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div style={{ fontSize: 12, color: "#475569", fontWeight: 700 }}>No tracker data yet — MoneyBot will guide you through setup below ↓</div>
          <button onClick={onViewFullTracker} style={btnStyle} className="btn-bounce">Manual Setup →</button>
        </div>
      );
    }
    const cats = trackerData.categories;
    const snap = trackerData.history[trackerData.history.length - 1].snapshot;
    let nw = 0, totalAssets = 0, totalDebts = 0;
    Object.entries(cats).forEach(([k, c]: [string, any]) => {
      const v = snap[k] || 0;
      if (c.isAsset) { nw += v; totalAssets += v; } else { nw -= v; totalDebts += v; }
    });
    const prev = trackerData.history.length > 1
      ? Object.entries(cats).reduce((s, [k, c]: [string, any]) => s + (c.isAsset ? 1 : -1) * (trackerData.history[trackerData.history.length - 2].snapshot[k] || 0), 0)
      : null;
    const trending = prev !== null ? (nw >= prev ? "▲ up" : "▼ down") : null;
    return (
      <div style={{ padding: "10px 14px", background: "#0f172a", borderBottom: "1px solid #1e293b", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, color: "#475569", fontWeight: 800, textTransform: "uppercase" as const, letterSpacing: 1 }}>Net Worth</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: nw >= 0 ? "#10b981" : "#ef4444", fontFamily: "Fredoka One" }}>
            {nw >= 0 ? "$" : "-$"}{Math.abs(Math.round(nw)).toLocaleString()}
          </div>
          <div style={{ fontSize: 11, color: "#475569", fontWeight: 700 }}>
            ${Math.round(totalAssets).toLocaleString()} assets · ${Math.round(totalDebts).toLocaleString()} debt
            {trending && <span style={{ marginLeft: 6, color: trending.startsWith("▲") ? "#10b981" : "#ef4444" }}>{trending}</span>}
          </div>
        </div>
        <button onClick={onViewFullTracker} style={btnStyle} className="btn-bounce">View Full →</button>
      </div>
    );
  }

  if (proContext === "budget") {
    if (!md) {
      return (
        <div style={{ padding: "10px 14px", background: "#0f172a", borderBottom: "1px solid #1e293b", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div style={{ fontSize: 12, color: "#475569", fontWeight: 700 }}>No budget set — MoneyBot will guide you through setup below ↓</div>
          <button onClick={onViewFullBudget} style={btnStyle} className="btn-bounce">Manual Setup →</button>
        </div>
      );
    }
    const income = md.income || 0;
    const h = computeHealth(computeCatsWithActual(md));
    const statusColor = h.status === "over" ? "#ef4444" : h.status === "close" ? "#f59e0b" : "#10b981";
    const statusLabel = h.status === "over" ? "🚨 Over Budget" : h.status === "close" ? "⚠️ Getting Close" : "✅ On Track";
    return (
      <div style={{ padding: "10px 14px", background: "#0f172a", borderBottom: "1px solid #1e293b", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, color: "#475569", fontWeight: 800, textTransform: "uppercase" as const, letterSpacing: 1 }}>{getMonthLabel(today)}</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <div style={{ fontSize: 20, fontWeight: 900, color: statusColor, fontFamily: "Fredoka One" }}>{Math.round(h.pct)}% used</div>
            <div style={{ fontSize: 11, fontWeight: 900, color: statusColor }}>{statusLabel}</div>
          </div>
          <div style={{ fontSize: 11, color: "#475569", fontWeight: 700 }}>
            ${Math.round(h.totalActual).toLocaleString()} spent · ${income.toLocaleString()} income
          </div>
        </div>
        <button onClick={onViewFullBudget} style={btnStyle} className="btn-bounce">View Full →</button>
      </div>
    );
  }

  // General — show both
  const nw = hasTracker
    ? (() => {
        const cats = trackerData.categories;
        const snap = trackerData.history[trackerData.history.length - 1].snapshot;
        return Object.entries(cats).reduce((s, [k, c]: [string, any]) => s + (c.isAsset ? 1 : -1) * (snap[k] || 0), 0);
      })()
    : null;
  const h = md ? computeHealth(computeCatsWithActual(md)) : null;

  return (
    <div style={{ padding: "10px 14px", background: "#0f172a", borderBottom: "1px solid #1e293b", display: "flex", gap: 8 }}>
      <button onClick={onViewFullTracker} className="btn-bounce"
        style={{ flex: 1, background: "#1e293b", border: "1px solid #334155", borderRadius: 10, padding: "8px 10px", cursor: "pointer", textAlign: "left" as const }}>
        <div style={{ fontSize: 9, color: "#475569", fontWeight: 800, textTransform: "uppercase" as const, letterSpacing: 1 }}>📈 Tracker</div>
        <div style={{ fontSize: 14, fontWeight: 900, color: nw !== null ? (nw >= 0 ? "#10b981" : "#ef4444") : "#334155", fontFamily: "Fredoka One" }}>
          {nw !== null ? `${nw >= 0 ? "$" : "-$"}${Math.abs(Math.round(nw)).toLocaleString()}` : "Not set up"}
        </div>
      </button>
      <button onClick={onViewFullBudget} className="btn-bounce"
        style={{ flex: 1, background: "#1e293b", border: "1px solid #334155", borderRadius: 10, padding: "8px 10px", cursor: "pointer", textAlign: "left" as const }}>
        <div style={{ fontSize: 9, color: "#475569", fontWeight: 800, textTransform: "uppercase" as const, letterSpacing: 1 }}>💰 Budget</div>
        <div style={{ fontSize: 14, fontWeight: 900, color: h ? (h.status === "over" ? "#ef4444" : h.status === "close" ? "#f59e0b" : "#10b981") : "#334155", fontFamily: "Fredoka One" }}>
          {h ? `${Math.round(h.pct)}% used` : "Not set up"}
        </div>
      </button>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ProChatScreen({
  trackerData, budgetData, proContext,
  onViewFullTracker, onViewFullBudget,
  onPersistTracker, onPersistBudget,
}: Props) {
  const today = new Date().toISOString().slice(0, 7);
  const hasTracker = trackerData?.history?.length > 0;
  const hasBudget = !!budgetData?.months?.[today];

  function computeInitGreeting(): string {
    if (proContext === "tracker") return hasTracker ? trackerDataGreeting(trackerData) : trackerSetupGreeting();
    if (proContext === "budget") return hasBudget ? budgetDataGreeting(budgetData, today) : budgetSetupGreeting(today);
    return generalGreeting(trackerData, budgetData, today);
  }

  function computeInitSetup(): SetupState | null {
    if (proContext === "tracker" && !hasTracker) return { mode: "tracker", step: 0, values: {} };
    if (proContext === "budget" && !hasBudget) return { mode: "budget", step: 0, values: {} };
    return null;
  }

  const [msgs, setMsgs] = useState<ChatMsg[]>([{ role: "bot", text: computeInitGreeting() }]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [setupState, setSetupState] = useState<SetupState | null>(computeInitSetup());
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, loading]);

  // ── Setup flow handlers ────────────────────────────────────────────────────

  function completeTrackerSetup(values: Record<string, number>) {
    const todayDate = new Date().toISOString().slice(0, 10);
    const cats = JSON.parse(JSON.stringify(EMPTY_CATS));
    const snap: Record<string, number> = {};
    Object.keys(cats).forEach((k) => { snap[k] = values[k] || 0; });

    onPersistTracker({ categories: cats, history: [{ date: todayDate, snapshot: snap, note: "Set up with MoneyBot" }] });

    let nw = 0, totalAssets = 0, totalDebts = 0;
    const assetLines: string[] = [];
    const debtLines: string[] = [];
    Object.entries(cats).forEach(([k, c]: [string, any]) => {
      const v = values[k] || 0;
      if (c.isAsset) {
        nw += v; totalAssets += v;
        if (v > 0) assetLines.push(`  ${c.emoji} ${c.label}: $${Math.round(v).toLocaleString()}`);
      } else {
        nw -= v; totalDebts += v;
        if (v > 0) debtLines.push(`  ${c.emoji} ${c.label}: $${Math.round(v).toLocaleString()}`);
      }
    });

    const lines = [
      "Your wealth tracker is built! 🎉",
      "",
      `📊 Net Worth: ${nw >= 0 ? "$" : "-$"}${Math.abs(Math.round(nw)).toLocaleString()}`,
      "",
      `💰 Assets — $${Math.round(totalAssets).toLocaleString()} total:`,
      ...(assetLines.length ? assetLines : ["  (none entered)"]),
      "",
      `💳 Debts — $${Math.round(totalDebts).toLocaleString()} total:`,
      ...(debtLines.length ? debtLines : ["  None — debt free! 🎉"]),
      "",
      totalDebts > 0
        ? nw >= 0
          ? "You're net-worth positive — you own more than you owe. Great foundation to build from!"
          : "Your net worth is currently negative — debt outweighs your assets. Focus on your highest-interest debt first to flip this."
        : "No debt at all — massive advantage. Focus on growing your investments and emergency fund.",
      "",
      "Tap View Full Tracker above to see your dashboard and update numbers over time. Or ask me anything about your finances!",
    ];

    setMsgs((m) => [...m, { role: "bot", text: lines.join("\n") }]);
    setSetupState(null);
  }

  function completeBudgetSetup(values: Record<string, number>) {
    const income = values.income || 0;
    const newCats: Record<string, { budget: number; actual: number }> = {};
    for (const cat of BUDGET_CATS) {
      newCats[cat.id] = { budget: Math.round(values[cat.id] || 0), actual: 0 };
    }

    const existing = budgetData || { months: {} };
    onPersistBudget({
      ...existing,
      months: { ...existing.months, [today]: { ...(existing.months?.[today] || {}), income, categories: newCats } },
    });

    const totalBudgeted = BUDGET_CATS.reduce((s, c) => s + (values[c.id] || 0), 0);
    const leftover = income - totalBudgeted;
    const pct = income > 0 ? Math.round((totalBudgeted / income) * 100) : 0;

    const lines = [
      `Your ${getMonthLabel(today)} budget is built! 🎉`,
      "",
      `💵 Income: $${income.toLocaleString()}/month`,
      "",
      "📊 Budget breakdown:",
      ...BUDGET_CATS.filter((c) => (values[c.id] || 0) > 0).map((c) => {
        const p = income > 0 ? Math.round(((values[c.id] || 0) / income) * 100) : 0;
        return `  ${c.emoji} ${c.label}: $${Math.round(values[c.id] || 0).toLocaleString()}${income > 0 ? ` (${p}% of income)` : ""}`;
      }),
      "",
      `Total budgeted: $${Math.round(totalBudgeted).toLocaleString()} (${pct}% of income)`,
      income > 0
        ? leftover > 0
          ? `Unallocated: $${Math.round(leftover).toLocaleString()} — consider putting this toward extra savings or debt payoff!`
          : leftover < 0
          ? `⚠️ Over-allocated by $${Math.round(Math.abs(leftover)).toLocaleString()} — your planned spending exceeds income. Trim some categories.`
          : "Perfectly allocated — every dollar has a job."
        : "",
      "",
      "Tap View Full Budget above to log actual spending throughout the month. Or ask me how to optimize this budget!",
    ].filter((l) => l !== undefined);

    setMsgs((m) => [...m, { role: "bot", text: lines.join("\n") }]);
    setSetupState(null);
  }

  function handleSetupInput(input: string) {
    if (!setupState) return;
    const steps = setupState.mode === "tracker" ? TRACKER_STEPS : BUDGET_STEPS;
    const cur = steps[setupState.step];
    const total = steps.length;

    setMsgs((m) => [...m, { role: "user", text: input }]);

    const numVal = parseAmount(input);
    if (numVal === null) {
      setMsgs((m) => [
        ...m,
        { role: "bot", text: `I need a number for that one. Just enter a dollar amount — or type 0 to skip.\n\nQuestion ${setupState.step + 1} of ${total}: ${cur.emoji} ${cur.label}` },
      ]);
      return;
    }

    const newValues = { ...setupState.values, [cur.key]: numVal };
    const nextStep = setupState.step + 1;

    if (nextStep >= total) {
      if (setupState.mode === "tracker") completeTrackerSetup(newValues);
      else completeBudgetSetup(newValues);
    } else {
      const next = steps[nextStep];
      const confirm = numVal > 0
        ? `${cur.emoji} $${Math.round(numVal).toLocaleString()} — got it ✓`
        : `${cur.emoji} No ${cur.label.toLowerCase()} — noted ✓`;
      setMsgs((m) => [
        ...m,
        { role: "bot", text: `${confirm}\n\nQuestion ${nextStep + 1} of ${total}:\n\n${next.q(newValues)}` },
      ]);
      setSetupState({ ...setupState, step: nextStep, values: newValues });
    }
  }

  // ── Normal chat ────────────────────────────────────────────────────────────

  async function send(text?: string) {
    const msg = (text ?? q).trim();
    if (!msg || loading) return;
    track("moneybot_asked");
    setQ("");

    if (setupState) {
      handleSetupInput(msg);
      return;
    }

    const history = msgs;
    setMsgs((m) => [...m, { role: "user", text: msg, kind: "chat" }]);
    setLoading(true);

    try {
      const answer = await askMoneyBot({
        question: msg,
        mode: "adult",
        context: buildFinancialContext(trackerData, budgetData) || null,
        history,
      });
      setMsgs((m) => [...m, { role: "bot", text: answer, kind: "chat" }]);
    } catch (err) {
      const reason = err instanceof MoneyBotError ? err.reason : "failed";
      const text = reason === "busy" ? BUSY_MSG : reason === "timeout" ? TIMEOUT_MSG : (getAnswer(msg) ?? OFFLINE_MSG) + previewDebug(err);
      setMsgs((m) => [...m, { role: "bot", text }]);
    } finally {
      setLoading(false);
    }
  }

  const chips = setupState
    ? []
    : proContext === "tracker"
    ? ["Am I on track?", "How to grow my net worth?", "What to prioritize?", "How to invest my savings?"]
    : proContext === "budget"
    ? ["Where am I overspending?", "Help me cut spending", "Am I on track?", "50/30/20 rule for me"]
    : ["Review my finances", "Am I on track?", "Debt payoff plan", "How to start investing"];

  return (
    <div style={{ fontFamily: "Nunito", display: "flex", flexDirection: "column", height: "calc(100vh - 70px)", background: "#0f172a" }}>

      {/* Header */}
      <div style={{ background: "linear-gradient(135deg,#1e3a5f,#0f172a)", padding: "12px 16px 14px", textAlign: "center", flexShrink: 0, borderBottom: "1px solid #1e293b" }}>
        <div style={{ fontSize: 30, lineHeight: 1 }}>🤖</div>
        <div style={{ fontSize: 17, fontWeight: 900, color: "#f1f5f9", fontFamily: "Fredoka One", marginTop: 2 }}>
          {proContext === "tracker" ? "📈 Tracker Assistant" : proContext === "budget" ? "💰 Budget Assistant" : "MoneyBot"}
        </div>
        <div style={{ fontSize: 10, color: "#475569", fontWeight: 700, marginTop: 2 }}>
          AI-powered · uses your real financial data
        </div>
      </div>

      {/* Mini summary */}
      <MiniSummary
        trackerData={trackerData} budgetData={budgetData} proContext={proContext}
        onViewFullTracker={onViewFullTracker} onViewFullBudget={onViewFullBudget}
        onPersistTracker={onPersistTracker} onPersistBudget={onPersistBudget}
      />

      {/* Setup progress bar */}
      {setupState && (
        <div style={{ padding: "8px 14px", background: "#1e293b", borderBottom: "1px solid #334155", display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: "#64748b", whiteSpace: "nowrap" as const }}>
            Step {setupState.step + 1} of {(setupState.mode === "tracker" ? TRACKER_STEPS : BUDGET_STEPS).length}
          </div>
          <div style={{ flex: 1, background: "#0f172a", borderRadius: 20, height: 6, overflow: "hidden" }}>
            <div style={{
              width: `${((setupState.step + 1) / (setupState.mode === "tracker" ? TRACKER_STEPS : BUDGET_STEPS).length) * 100}%`,
              height: "100%", background: "linear-gradient(90deg,#0ea5e9,#8b5cf6)", borderRadius: 20, transition: "width 0.4s",
            }} />
          </div>
          <div style={{ fontSize: 11, fontWeight: 800, color: "#0ea5e9", whiteSpace: "nowrap" as const }}>
            {setupState.mode === "tracker" ? "Wealth Tracker" : "Budget"}
          </div>
        </div>
      )}

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "14px", display: "flex", flexDirection: "column", gap: 10 }}>
        {msgs.map((m, i) => (
          <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-end", flexDirection: m.role === "user" ? "row-reverse" : "row" }}>
            {m.role === "bot" && <div style={{ fontSize: 22, flexShrink: 0, lineHeight: 1 }}>🤖</div>}
            <div style={{
              maxWidth: "84%",
              background: m.role === "user" ? "linear-gradient(135deg,#0ea5e9,#8b5cf6)" : "#1e293b",
              color: m.role === "user" ? "#fff" : "#f1f5f9",
              borderRadius: m.role === "user" ? "20px 20px 4px 20px" : "20px 20px 20px 4px",
              padding: "11px 15px", fontSize: 14, fontWeight: 700, lineHeight: 1.7,
              border: m.role === "bot" ? "1px solid #334155" : "none",
              whiteSpace: m.role === "user" ? "pre-wrap" : "normal",
            }}>
              {m.role === "bot" ? <ChatMarkdown text={m.text} /> : m.text}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{ fontSize: 22, lineHeight: 1 }}>🤖</div>
            <div style={{ background: "#1e293b", borderRadius: 20, padding: "13px 16px", border: "1px solid #334155", display: "flex", gap: 5 }}>
              {[0, 1, 2].map((i) => (
                <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: "#0ea5e9", animation: `bounce 0.9s ${i * 0.18}s infinite` }} />
              ))}
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Input area */}
      <div style={{ background: "#1e293b", borderTop: "1px solid #334155", padding: "8px 12px 12px", flexShrink: 0 }}>
        {/* Analyze button — shown when there's data and not in setup flow */}
        {!setupState && (hasTracker || hasBudget) && (
          <button
            onClick={() => send("Based on my financial data above, I want to hit my goals faster. Please: 1) identify what's holding me back most, 2) give me a specific revised monthly budget with exact dollar amounts I should adjust, 3) tell me the single most impactful action I should take this month.")}
            disabled={loading}
            style={{
              width: "100%", marginBottom: 8,
              background: "linear-gradient(135deg,#10b981,#0ea5e9)",
              border: "none", borderRadius: 14, padding: "11px 16px",
              fontSize: 13, fontWeight: 900, color: "#fff", cursor: "pointer",
              opacity: loading ? 0.5 : 1, transition: "opacity 0.2s",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
            <span>📊</span> Analyze My Budget &amp; Adjust for Goals
          </button>
        )}
        {chips.length > 0 && (
          <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 8, scrollbarWidth: "none" as const }}>
            {chips.map((c) => (
              <button key={c} onClick={() => send(c)}
                style={{ flexShrink: 0, background: "#0f172a", border: "1.5px solid #334155", borderRadius: 20, padding: "6px 13px", fontSize: 12, fontWeight: 800, color: "#0ea5e9", cursor: "pointer", whiteSpace: "nowrap" }}>
                {c}
              </button>
            ))}
          </div>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <input value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={setupState ? "Type your answer... (e.g. 3500)" : "Ask MoneyBot about your finances... 💬"}
            style={{ flex: 1, background: "#0f172a", border: "1.5px solid #334155", borderRadius: 14, padding: "11px 14px", fontSize: 14, fontWeight: 700, outline: "none", fontFamily: "Nunito", color: "#f1f5f9" }}
          />
          <button onClick={() => send()} disabled={!q.trim() || loading} className="btn-bounce"
            style={{ background: "linear-gradient(135deg,#0ea5e9,#8b5cf6)", border: "none", borderRadius: 14, width: 48, height: 48, color: "#fff", fontSize: 22, cursor: "pointer", opacity: !q.trim() || loading ? 0.4 : 1, transition: "opacity 0.2s", flexShrink: 0 }}>
            →
          </button>
        </div>
      </div>
    </div>
  );
}
